import type { SubscriptionResponse } from '@cvo/domain/api';
import { isAfter, isFuture, startOfDay } from 'date-fns';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

export function useSubscription(subscription: MaybeRefOrGetter<SubscriptionResponse | null | undefined>) {
  const startDate = computed<Date | null>(() => {
    const subscriptionValue = toValue(subscription);

    if (!subscriptionValue?.plan.startDate) return null;
    return new Date(subscriptionValue.plan.startDate!);
  });

  const endOfTermDate = computed<Date | null>(() => {
    const subscriptionValue = toValue(subscription);

    const periodEnd = subscriptionValue?.quota?.periodEnd;
    return periodEnd ? new Date(periodEnd) : null;
  });

  const commitmentEndDate = computed<Date | null>(() => {
    const subscriptionValue = toValue(subscription);

    const endCommitment = subscriptionValue?.commitment?.endDate;
    return endCommitment ? new Date(endCommitment) : null;
  });

  const gracePeriodEndDate = computed<Date | null>(() => {
    const subscriptionValue = toValue(subscription);

    const endGracePeriod = subscriptionValue?.gracePeriod?.endDate;
    return endGracePeriod ? new Date(endGracePeriod) : null;
  });

  const isInGracePeriod = computed(() => {
    if (!gracePeriodEndDate.value) return false;

    return isFuture(startOfDay(gracePeriodEndDate.value));
  });

  const isCancelling = computed(() => {
    const subscriptionValue = toValue(subscription);

    return subscriptionValue?.cancellation?.status === 'PENDING' || subscriptionValue?.cancellation?.status === 'COMPLETED';
  });

  const minimumCancellationDate = computed<Date | null>(() => {
    if (commitmentEndDate.value && endOfTermDate.value && isAfter(commitmentEndDate.value, endOfTermDate.value)) {
      return commitmentEndDate.value;
    }
    return endOfTermDate.value;
  });

  const cancellationDate = computed<Date | null>(() => {
    const subscriptionValue = toValue(subscription);

    if (subscriptionValue?.cancellation?.effectiveDate) {
      return new Date(subscriptionValue.cancellation.effectiveDate);
    }

    return null;
  });

  return {
    startDate,
    cancellationDate,
    minimumCancellationDate,
    endOfTermDate,
    commitmentEndDate,
    gracePeriodEndDate,
    isInGracePeriod,
    isCancelling,
  };
}